import { RefObject, useEffect } from "react";

import { UseFormReturn } from "react-hook-form";

import { useLiveFormEditorContext } from "../common/LiveFormEditorContext";
import { AnyStepFormData } from "../Steps/AnyStepFormData";

import { ScrollContext } from "./getScrollTopStepTarget";
import { useScrollPreviewWithArrow } from "./useScrollPreviewWithArrow";

export const useAnimatePreviewOnFormEvents = <T extends AnyStepFormData>(
  formContainerRef: RefObject<HTMLElement | null>,
  form?: UseFormReturn<T>,
  /** Optional callback to provide scroll context for dynamic scroll targets */
  getScrollContext?: () => ScrollContext
) => {
  const { currentStepIndex: currentStep, previewDevice } = useLiveFormEditorContext();

  const { scrollAndUpdateArrow } = useScrollPreviewWithArrow();

  // 1. Animate preview when the step mount/changes and viewport mode changes
  useEffect(() => {
    // Delay the anim to make sure the preview is mounted
    const timeoutId = setTimeout(() => {
      scrollAndUpdateArrow(getScrollContext?.());
    }, 700);

    return () => clearTimeout(timeoutId);
  }, [currentStep, previewDevice, scrollAndUpdateArrow, getScrollContext]);

  // 2. Animate preview when form values change
  useEffect(() => {
    const subscription = form?.watch(() => {
      scrollAndUpdateArrow(getScrollContext?.());
    });

    return () => subscription?.unsubscribe();
  }, [form, scrollAndUpdateArrow, currentStep, getScrollContext]);

  // 3. Animate preview when focusing in the form
  useEffect(() => {
    const formElement = formContainerRef.current;
    if (!formElement) {
      return;
    }

    const handleFocusIn = () => scrollAndUpdateArrow(getScrollContext?.());

    formElement.addEventListener("focusin", handleFocusIn);
    return () => {
      formElement.removeEventListener("focusin", handleFocusIn);
    };
  }, [formContainerRef, scrollAndUpdateArrow, getScrollContext]);

  // 4. Animate preview when the viewport mode changes
  useEffect(() => {
    scrollAndUpdateArrow(getScrollContext?.());
  }, [previewDevice, scrollAndUpdateArrow, getScrollContext]);
};
